import { CalendarBlank, List } from "@phosphor-icons/react"
import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router"
import { useMemo } from "react"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import { Module } from "~/components/design-system/pages"
import { type HeaderTab } from "~/components/reusable/HeaderTabs"
import { useFilteredVariants } from "~/hooks/useVariants"
import { startCase, lowerCase } from "lodash"
import { useSkuStore } from "~/providers/sku-store-provider"
import { InactiveVariantsWithIncomingStockWarning } from "~/components/purchaseOrder/incomingOrders/InactiveVariantsWithIncomingStockWarning"
import { IncomingOrdersProvider } from "~/context/IncomingOrdersProvider"
import { api } from "~/utils/api"
import { IncomingOrderStatus } from "~/constants/incomingOrdersStatus"
import { useFilters } from "~/hooks/useFilters"
import { getIncomingOrderExtraDataFilters } from "~/utils/incomingOrdersUtils"
import { filterVariants } from "~/utils/filter"
import { useFiscalWeek } from "~/hooks/useFiscalWeek"
import { fiscalYearFromLabel, fiscalYearStartDate } from "~/utils/fiscalCalendar"
import { getStartOfYearUTC } from "~/utils/datetime"
import {
  useIncomingOrdersCalendarYear,
  useIncomingOrdersShipmentMethod,
  useIncomingOrdersStatuses,
} from "~/hooks/useIncomingOrdersFilters"

export const Route = createFileRoute("/_auth/procurement/incoming-orders/_layout")({
  component: RouteComponent,
})

enum IncomingTab {
  calendar = "Calendar",
  list = "List",
}

const tabs: HeaderTab[] = [
  {
    label: IncomingTab.calendar,
    path: "/procurement/incoming-orders/calendar",
    icon: <CalendarBlank />,
  },
  { label: IncomingTab.list, path: "/procurement/incoming-orders/list", icon: <List /> },
]

function RouteComponent() {
  const { shipmentMethod } = useIncomingOrdersShipmentMethod()

  const { selectedStatuses } = useIncomingOrdersStatuses()
  const { selectedYear } = useIncomingOrdersCalendarYear()
  const showArrived = selectedStatuses.includes(IncomingOrderStatus.Arrived)

  const baseVariants = useFilteredVariants(undefined)

  const { filters } = useFilters()
  // PO note (and other incoming-order-extradata) filters must be re-applied to the orders
  // rendered here: useFilteredVariants only filters the SKU store's orders to decide which
  // variants survive, but the lines below rebuild incomingOrders from the server-fetched
  // detail/calendar maps, which would otherwise leak non-matching orders (REKD-619).
  const incomingOrderExtraDataFilters = useMemo(
    () => getIncomingOrderExtraDataFilters(filters),
    [filters],
  )

  // useLocation pathname is synchronous with the URL — useMatchRoute reads resolvedLocation,
  // which lags during tab transitions and can flip the active map to thin data.
  const { pathname } = useLocation()
  const isCalendarRoute = pathname.endsWith("/incoming-orders/calendar")

  const { fiscalConfig } = useFiscalWeek()
  // Fetch a window covering both the calendar year (monthly/quarterly views) and the fiscal year
  // (weekly view, anchored ≠ Jan 1), so every displayed calendar column is populated regardless of
  // the active view. startDate/endDate override `year` server-side.
  const incomingRange = useMemo(() => {
    const calStart = getStartOfYearUTC(selectedYear)
    const calEnd = getStartOfYearUTC(selectedYear + 1)
    if (!fiscalConfig) return { startDate: calStart, endDate: calEnd }
    const fiscalYear = fiscalYearFromLabel(selectedYear, fiscalConfig)
    const fyStart = fiscalYearStartDate(fiscalYear, fiscalConfig)
    const fyEnd = fiscalYearStartDate(fiscalYear + 1, fiscalConfig)
    return {
      startDate: fyStart < calStart ? fyStart : calStart,
      endDate: fyEnd > calEnd ? fyEnd : calEnd,
    }
  }, [selectedYear, fiscalConfig])

  const { data: fastIncomingMap, isLoading: fastIncomingLoading } =
    api.variantTableData.incomingOrders.useQuery(
      { startDate: incomingRange.startDate, endDate: incomingRange.endDate },
      { enabled: isCalendarRoute },
    )
  const { data: fastArrivedMap, isLoading: fastArrivedLoading } =
    api.variantTableData.incomingOrders.useQuery(
      {
        status: "arrived",
        startDate: incomingRange.startDate,
        endDate: incomingRange.endDate,
      },
      { enabled: isCalendarRoute && showArrived },
    )
  // List view doesn't year-filter — production orders are point-in-time and the list will move
  // server-side once tenant volume warrants it.
  const { data: detailIncomingMap, isLoading: detailIncomingLoading } =
    api.variantTableData.incomingOrderDetailsMap.useQuery(undefined, { enabled: !isCalendarRoute })
  const { data: detailArrivedMap, isLoading: detailArrivedLoading } =
    api.variantTableData.incomingOrderDetailsMap.useQuery(
      { status: "arrived" },
      { enabled: !isCalendarRoute && showArrived },
    )

  const activeIncomingMap = isCalendarRoute ? fastIncomingMap : detailIncomingMap
  const activeArrivedMap = isCalendarRoute ? fastArrivedMap : detailArrivedMap

  // Disabled queries report isLoading=false, so gating on just the active route's query is safe.
  const incomingOrdersLoading = isCalendarRoute
    ? fastIncomingLoading || (showArrived && fastArrivedLoading)
    : detailIncomingLoading || (showArrived && detailArrivedLoading)

  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const { data: warehouses } = api.warehouse.get.useQuery()
  const { data: hasExternalCollectionId } = api.purchaseOrder.hasExternalCollectionId.useQuery()
  const { data: hasShipmentMethod } = api.purchaseOrder.hasShipmentMethod.useQuery()
  const { data: hasVendorId } = api.purchaseOrder.hasVendorId.useQuery()

  const filteredVariants = useMemo(() => {
    const variantsWithOrders = baseVariants.map((variant) => {
      const incoming = activeIncomingMap?.get(variant.variantId) ?? []
      const arrived = showArrived ? (activeArrivedMap?.get(variant.variantId) ?? []) : []
      let allOrders = [...incoming, ...arrived]

      allOrders = allOrders.filter((order) =>
        selectedWarehouseId ? order.warehouseId === selectedWarehouseId : true,
      )

      if (shipmentMethod) {
        allOrders = allOrders.filter(
          (order) => order.shipmentMethod?.toLowerCase() === shipmentMethod.toLowerCase(),
        )
      }

      return {
        ...variant,
        incomingOrders: allOrders,
      }
    })

    // Apply the PO-note (incoming-order-extradata) filters through the canonical filterVariants
    // path so the rebuilt orders are pruned exactly as everywhere else. Passing only the
    // extradata filters keeps the regular-filter and vendor branches no-ops (regular filters
    // were already applied by useFilteredVariants on the store variants above).
    if (incomingOrderExtraDataFilters.length === 0) return variantsWithOrders
    return filterVariants(incomingOrderExtraDataFilters, variantsWithOrders)
  }, [
    baseVariants,
    activeIncomingMap,
    activeArrivedMap,
    showArrived,
    selectedWarehouseId,
    shipmentMethod,
    incomingOrderExtraDataFilters,
  ])

  const shipmentMethodOptions = useMemo(() => {
    const methods = new Set<string>()
    activeIncomingMap?.forEach((orders) => {
      orders.forEach((order) => {
        if (order.shipmentMethod) {
          methods.add(order.shipmentMethod)
        }
      })
    })
    return Array.from(methods).map((method) => ({
      value: method,
      label: startCase(lowerCase(method)),
    }))
  }, [activeIncomingMap])

  const hasMultipleWarehouses = useMemo(() => {
    return warehouses && warehouses.length > 1 ? true : false
  }, [warehouses])

  return (
    <IncomingOrdersProvider
      variants={filteredVariants}
      hasShipmentMethod={hasShipmentMethod ?? false}
      hasMultipleWarehouses={hasMultipleWarehouses}
      hasExternalCollectionId={hasExternalCollectionId ?? false}
      hasVendorId={hasVendorId ?? false}
      shipmentMethodOptions={shipmentMethodOptions}
      isLoading={incomingOrdersLoading}
    >
      <Module
        breadcrumb={<BreadcrumbWrapper />}
        actions={<InactiveVariantsWithIncomingStockWarning />}
        tabs={{ items: tabs, path: "/procurement/incoming-orders" }}
      >
        <Outlet />
      </Module>
    </IncomingOrdersProvider>
  )
}
